import { format } from 'date-fns';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  useTheme,
} from '@mui/material';
import React, { forwardRef, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UIContext } from '../../../context/UIContext';
import { ReactComponent as WhiteSuccessIcon } from '../../../img/success-white.svg';
import FileDocumentCheck from '../../../components/FileDocumentCheckSVG';
import { ReactComponent as SuccessIcon } from '../../../img/success.svg';
import objectArrayToCSV from '../../../helpers/objectArrayToCSV';
import { TroncRow } from '../../../models/tronc';

export interface ExportModalProps {
  start: boolean;
  onComplete: () => void;
  rows: TroncRow[];
  closeAction: () => void;
}

export const ExportModal = forwardRef<HTMLDivElement, ExportModalProps>(
  ({ start, onComplete, rows, closeAction } : ExportModalProps, ref) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [filename, setFilename] = useState<string>();
  const { colorMode } = useContext(UIContext);
  const theme = useTheme();
  const backgroundColor = theme.palette.background.paper;

  const createFileUri = () => {
    const excludeColumns: (keyof TroncRow)[] = ["noOfShiftRecords"];
    const transformer = (key: string, val: unknown) => {
      if (["hours", "gratuity", "serviceCharge", "totalTronc"].includes(key)) {
        return (val as number ?? 0).toFixed(2);
      }
      return val;
    };

    const blob = new Blob([objectArrayToCSV(rows, ",", "/", excludeColumns, transformer)], { type: "application/octet-stream" });
    return URL.createObjectURL(blob);
  };

  const generateNewFilename = useCallback(() => {
    const ts = format(new Date(), 'yyyyMMdd_kkmmss');
    setFilename(`${t('export.troncReport')}_${ts}.csv`);
  }, [t]);

  useEffect(() => {
    if (!start) {
      setFilename(undefined);
      setVisible(false);
      return;
    }
    generateNewFilename();
    setVisible(true);
  }, [start, generateNewFilename]);

  const onClose = () => {
    setVisible(false);
    onComplete?.();
    setFilename(undefined);
    closeAction();
  };

  const onDownload = () => {
    const link = document.createElement('a');
    link.href = createFileUri();
    link.setAttribute('target', '_blank');
    link.setAttribute('download', filename || 'export.csv');
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    onClose();
  };

  if (!visible) return null;

  return (
    <div ref={ref} style={{backgroundColor, borderTopRightRadius: '20px', borderTopLeftRadius: '20px'}}>
    <Box
      sx={{
        width: '100%',
        height: '70%',
        borderTopLeftRadius: '20px',
        borderTopRightRadius: '20px',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'center',
        marginBottom: '16px'
      }}
    >
      <Box sx={{ margin: '16px', marginTop: '24px' }}>
        {colorMode === 'light'
          ? <SuccessIcon style={{ width: "56px", height: "56px" }} />
          : <WhiteSuccessIcon style={{ width: "56px", height: "56px" }} />
        }

        <Typography variant='h2' sx={{ marginY: '8px' }}>
          {t('export.exportingTronc')}
        </Typography>

        <Typography variant='body2' sx={{ marginBottom: '42px' }}>
          {t('export.yourReportIsAvailable')}
          <br />
          {t('export.itIsNowReady')}
        </Typography>

        <Box>
          <Card variant="outlined" sx={{ width: '100%' }}>
            <CardContent sx={{ padding: '16px !important' }}>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'center',
                justifyContent: 'center'
              }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
                  <FileDocumentCheck />
                  <Typography component="span" sx={{ fontWeight: 400, fontSize: '14px', marginLeft: '10px'}}>
                    {filename || 'export.csv'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Box sx={{ marginTop: 'auto', marginX: '16px', backgroundColor: 'background.paper' }}>
        <Button
          sx={{ width: '100%', marginTop: 2 }}
          variant="contained"
          size="large"
          onClick={onDownload}
          autoFocus
        >
          {t('actions.download')}
        </Button>
        <Button
          sx={{ width: '100%', marginTop: 2 }}
          variant="outlined"
          size="large"
          onClick={onClose}
        >
          {t('actions.cancel')}
        </Button>
      </Box>
    </Box>
    </div>
  );
});
